import type { SavedCypherTree } from '@query/services/api';
import {
  removeItemIdsFromSavedCypherTree,
  useBatchDeleteSavedCypherItemsMutation,
  useLazyGetSavedCypherTreeByIdQuery,
  useUpdateSavedCypherTreeMutation,
} from '@query/services/api';
import { log } from '@query/services/logging';
import { useCallback } from 'react';

type BatchDeleteSavedCypherItemsArg = {
  savedCypherTreeId: string;
  itemIds: string[];
};
type BatchDeleteSavedCypherItemsFn = (arg: BatchDeleteSavedCypherItemsArg) => Promise<void>;

/**
 * Custom hook that encapsulates the logic to delete saved cypher items and update the tree structures'
 * array of ordered items.
 */
export const useCommonBatchDeleteSavedCypherItemsMutation = () => {
  const [getSavedCypherTreeById, getSavedCypherTreeByIdState] = useLazyGetSavedCypherTreeByIdQuery();
  const [batchDeleteSavedCypherItems, batchDeleteSavedCypherItemsRes] = useBatchDeleteSavedCypherItemsMutation();
  const [updateSavedCypherTree, updateSavedCypherTreeRes] = useUpdateSavedCypherTreeMutation();

  const batchDeleteSavedCypherItemsInternal: BatchDeleteSavedCypherItemsFn = useCallback(
    async (arg) => {
      const { savedCypherTreeId } = arg;
      let tree: SavedCypherTree | undefined;
      try {
        tree = await getSavedCypherTreeById({ id: savedCypherTreeId }).unwrap();
      } catch (err) {
        log.error('[useCommonBatchDeleteSavedCypherItemsMutation] Error fetching saved cypher tree', err);
        throw err;
      }

      let deletedIds: string[] = [];
      try {
        const res = await batchDeleteSavedCypherItems({
          itemIds: arg.itemIds,
          savedCypherTreeId,
        }).unwrap();
        deletedIds = [...res];
      } catch (err) {
        log.error('[useCommonBatchDeleteSavedCypherItemsMutation] Error deleting tree items', err);
        throw err;
      }

      try {
        const { orderedItems } = removeItemIdsFromSavedCypherTree(tree, deletedIds);

        await updateSavedCypherTree({
          id: savedCypherTreeId,
          savedCypherTree: { orderedItems },
        });
      } catch (err) {
        log.error('[useCommonBatchDeleteSavedCypherItemsMutation] Error updating tree', err);
        throw err;
      }
    },
    [batchDeleteSavedCypherItems, updateSavedCypherTree, getSavedCypherTreeById],
  );

  const res = [
    batchDeleteSavedCypherItemsInternal,
    {
      isLoading:
        getSavedCypherTreeByIdState.isLoading ||
        updateSavedCypherTreeRes.isLoading ||
        batchDeleteSavedCypherItemsRes.isLoading,
      isError:
        getSavedCypherTreeByIdState.isError ||
        updateSavedCypherTreeRes.isError ||
        batchDeleteSavedCypherItemsRes.isError,
    },
  ] as const;

  return res;
};
